import * as Sentry from "@sentry/react";

let aggregatorURL = import.meta.env.VITE_AGGREGATOR_URL;
if (aggregatorURL === undefined && import.meta.env.DEV) {
  aggregatorURL = "http://localhost:3300";
}
const aggregatorKey = import.meta.env.VITE_AGGREGATOR_KEY;

/**
 * Fetch the public ranking: json array of projects: `{rank, project_slug, votes}`.
 * When in import.meta.env.DEV and no key is provided, the mock data is used.
 *
 * @returns {Promise<import("./ranking").RankingRow[]|null>} The ranking, or
 *   `null` on error so the caller can tell a failure from an empty ranking.
 */
export async function fetchRanking() {
  if (!aggregatorURL || !aggregatorKey) {
    if (!import.meta.env.DEV) return null;

    return (await import("./data/mock_ranking.json")).default;
  }

  try {
    const response = await fetch(`${aggregatorURL}/burgerbegroting/ranking`, {
      headers: { Authorization: `Bearer ${aggregatorKey}` },
    });

    if (!response.ok) {
      Sentry.captureMessage(`Aggregator ranking failed: ${response.status}`);
      return null;
    }

    return await response.json();
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
}

/**
 * Fetch the per-voter project queue from the aggregator: the given project ids
 * ordered least-globally-viewed first (random tie-break). Generated once per
 * voter at deck start and stored in the answers, so resumes reuse it.
 *
 * @param {string[]} projectIds - Bare project ids (no "proj_" prefix).
 * @returns {Promise<string[]|null>} The ordered queue, or `null` on error so
 *   the caller can fall back to catalog order.
 */
export async function fetchProjectQueue(projectIds) {
  if (!aggregatorURL || !aggregatorKey) return null;

  try {
    const response = await fetch(`${aggregatorURL}/burgerbegroting/queue`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        Authorization: `Bearer ${aggregatorKey}`,
      },
      body: JSON.stringify({ project_ids: projectIds }),
    });

    if (!response.ok) {
      Sentry.captureMessage(
        `Aggregator queue generation failed: ${response.status}`,
      );
      return null;
    }

    const { queue } = await response.json();
    return Array.isArray(queue) && queue.length > 0 ? queue : null;
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
}
